import { ImagePlus } from "@tamagui/lucide-icons";
import * as DocumentPicker from "expo-document-picker";
import {
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytes,
} from "firebase/storage";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import { log } from "../../ui/log";
import { storage } from "../firebaseInit";
import { trackEvent } from "../trackEvent";
import { usePerformance } from "../usePerformance";
import { getImageInfo } from "../utils";
import { CropImage } from "./CropImage";

function readFile(file: File): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

function verifyNonExistentRef(refStorageString: string): Promise<void> {
  let tries = 0;
  return new Promise((resolve, reject) => {
    async function testVerify() {
      const imageRef = ref(storage, refStorageString);

      tries++;
      if (tries > 5) {
        reject();
      }

      // Get the meta Data
      void getMetadata(imageRef)
        .then((metaData) => {
          log.dev("Still got metadata,", refStorageString, metaData);
        })
        .catch((error) => {
          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          log.dev("Got error code", error.code);
          switch (error.code) {
            case "storage/object-not-found":
              // File doesn't exist
              log.dev("Did not find metadata anymore, resize done!");
              clearInterval(i);
              resolve();

              break;
            case "storage/unauthorized":
              // User doesn't have permission to access the object
              break;
            case "storage/canceled":
              // User canceled the upload
              break;

            // ...

            case "storage/unknown":
              // Unknown error occurred, inspect the server response
              break;
          }
        });
    }

    const i = setInterval(testVerify, 2000);
  });
}

export function useUploadAvatar(
  uploadStateCallback: (isUploading: boolean) => void,
  uploadComplete: (url: string) => Promise<void>,
  userUid?: string,
  hasAvatar?: boolean,
) {
  const performanceMeasure = usePerformance("UPLOAD_AVATAR");
  const { t } = useTranslation();

  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const uploadFile = React.useCallback(
    async (uploadCroppedFile: Blob) => {
      const storagePath = "avatars";

      if (!userUid) {
        log.dev("Error - no user set");
        return;
      }

      performanceMeasure.start();
      uploadStateCallback(true);
      setIsUploading(true);

      try {
        // Create a reference to 'images/mountains.jpg'
        const storageRef = ref(storage, `${storagePath}/${userUid}`);

        const snapshot = await uploadBytes(storageRef, uploadCroppedFile);
        log.dev("Uploaded file!", snapshot);

        const url = await getDownloadURL(snapshot.ref); // include token for cache busts.split("&")[0];

        log.dev("got url..", url);
        log.dev("sizes", getImageInfo(url));

        // Wait for the resizes to be available (ie original not found)
        await verifyNonExistentRef(`${storagePath}/${userUid}`);

        await uploadComplete(url);
        performanceMeasure.stop();
        uploadStateCallback(false);

        trackEvent("user_avatar_uploaded");
      } catch (e) {
        console.error("Got invalid select file", e);
        uploadStateCallback(false);
        trackEvent("user_avatar_error");
      }

      setImageSrc(null);
      setIsUploading(false);
    },
    [uploadStateCallback, userUid, uploadComplete, performanceMeasure],
  );

  const selectFile = React.useCallback(async () => {
    if (!userUid) {
      log.dev("Error - no user set");
      return;
    }

    const result = await DocumentPicker.getDocumentAsync({
      type: "image/*",
    });

    // Only web compatible?
    const file = result.output?.[0];

    if (!file) {
      log.dev("No file found");
      return;
    }

    const imageDataUrl = await readFile(file);
    if (imageDataUrl) {
      setImageSrc(imageDataUrl as string);
    }
  }, [userUid]);

  return (
    <>
      <Button
        type={hasAvatar ? "secondary" : "primary"}
        isLoading={isUploading}
        icon={ImagePlus}
        onPress={selectFile}
      >
        {hasAvatar ? t("replace_photo") : t("upload_photo")}
      </Button>

      {imageSrc && !isUploading && (
        <CropImage
          image={imageSrc}
          aspect={1}
          showGrid={false}
          cropShape={"round"}
          onClose={() => {
            setImageSrc(null);
            setIsUploading(false);
          }}
          onCropDone={async (f: Blob | null) => {
            if (f) {
              await uploadFile(f);
            }
          }}
        />
      )}
    </>
  );
}
